// src/screens/TopicPractice.jsx
// Themen-Wochen der Übung „Redewendungen“ (Zahlen, Wochentage, Uhrzeit …).
// Ablauf (Kennenlernen → Abfrage-Runden → große Tests) steht in
// src/data/topicSession.js, die Wochen und Blöcke in phraseSchedule.js.
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../App.css";
import { supabase } from "../supabaseClient";
import {
  loadPhraseProgress,
  savePhraseProgress,
} from "../data/phraseProgressStorage";
import { loadTopics } from "../data/loadTopics";
import { languageLabel, parseLanguageId } from "../data/languages";
import { getPhrasesPerWeek } from "../data/phraseSchedule";
import {
  allowedTestErrors,
  answerQuestion,
  confirmIntro,
  continueAfterTest,
  currentItemIndex,
  gradeTest,
  nextQuestion,
  questionKind,
  startTopicSession,
  topicWeekProgress,
} from "../data/topicSession";
import { isSpeechSupported, speak } from "../utils/speech";
import {
  Badge,
  Button,
  Note,
  OptionCard,
  Panel,
  ScreenHeader,
  Stack,
  TextInput,
} from "../components/ui";

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Eingaben tolerant vergleichen (Groß/klein, Akzente, Satzzeichen)
// ’ = ', Bindestrich = Leerzeichen (vingt-et-un = vingt et un)
function normalize(str) {
  return (str || "")
    .toLowerCase()
    .replace(/[’‘`´]/g, "'")
    .replace(/-/g, " ")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[¿¡?!.,]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

// Bestandene Tests merkt sich das Gerät pro Woche, damit man nach einer
// Pause nicht von vorne anfängt
function storageKey(profile, langId, pace, week) {
  return `fluent_topic_progress_${profile}_${langId}_${pace}_w${week}`;
}

function loadLocalPasses(key) {
  try {
    const raw = window.localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return {
      passed: Array.isArray(parsed?.passed) ? parsed.passed : [],
      finalPassed: !!parsed?.finalPassed,
    };
  } catch {
    return { passed: [], finalPassed: false };
  }
}

function saveLocalPasses(key, passed, finalPassed) {
  try {
    window.localStorage.setItem(key, JSON.stringify({ passed, finalPassed }));
  } catch {
    // ohne Speicher geht es trotzdem weiter
  }
}

function SpeakButton({ text, lang }) {
  if (!isSpeechSupported()) return null;
  return (
    <button
      type="button"
      className="speak-button"
      aria-label="Aussprache anhören"
      title="Aussprache anhören"
      onClick={() => speak(text, lang)}
    >
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
        <path
          d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

export default function TopicPractice({
  language,
  pace,
  profileId,
  weekIndex,
  weekPlan,
  onBack,
}) {
  const langId = language || "es-de";
  const learningLang = parseLanguageId(langId)?.source || "es";
  const langLabel = languageLabel(learningLang);
  const paceValue = pace || "medium";
  const profile = profileId || "default";
  const currentWeek = weekIndex || 1;
  const weeklyTarget = getPhrasesPerWeek(paceValue);
  const localKey = storageKey(profile, langId, paceValue, currentWeek);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  // gespeicherter Wochenwert aus Supabase (wird nie verkleinert)
  const savedCountRef = useRef(0);

  const [session, setSession] = useState(null);

  // Abfrage: Auswahl oder Eintippen
  const [selected, setSelected] = useState(null);
  const [typed, setTyped] = useState("");
  const [checked, setChecked] = useState(false);
  const [correct, setCorrect] = useState(false);

  // Großer Test
  const [testInputs, setTestInputs] = useState({});
  const [testResult, setTestResult] = useState(null); // { results, passed, errors }
  const testInputRefs = useRef([]);

  // Laden: Einträge, Login, gespeicherter Fortschritt
  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError(null);
      try {
        const items = await loadTopics(learningLang);

        const { data: userData } = await supabase.auth.getUser();
        const uid = userData?.user?.id || null;
        let remoteCount = null;
        if (uid) {
          remoteCount = await loadPhraseProgress(
            uid,
            profile,
            langId,
            paceValue,
            currentWeek
          );
        }
        if (cancelled) return;

        let { passed, finalPassed } = loadLocalPasses(localKey);
        const blockKeys = weekPlan.blocks.map((b) => b.key);
        if (remoteCount !== null) {
          savedCountRef.current = remoteCount;
          const share = weeklyTarget / (blockKeys.length + 1);
          const remoteTests = Math.round(remoteCount / share);
          if (remoteCount === 0) {
            // in Supabase zurückgesetzt: Gerätestand verwerfen
            passed = [];
            finalPassed = false;
          } else if (remoteTests > passed.length + (finalPassed ? 1 : 0)) {
            // auf einem anderen Gerät weiter: Blöcke der Reihe nach als bestanden
            passed = blockKeys.slice(0, Math.min(remoteTests, blockKeys.length));
            finalPassed = remoteTests > blockKeys.length;
          }
        }

        setUserId(uid);
        setSession(startTopicSession(weekPlan, items, passed, finalPassed));
      } catch (e) {
        console.warn("TopicPractice: init failed:", e);
        if (!cancelled) setError(e?.message || "Unbekannter Fehler beim Laden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [profile, langId, learningLang, paceValue, currentWeek, weekPlan, weeklyTarget, localKey]);

  const itemIdx = session ? currentItemIndex(session) : null;
  const item = session && itemIdx !== null ? session.items[itemIdx] : null;
  const kind = session && session.mode === "quiz" ? questionKind(session) : null;

  // Auswahl: richtige Antwort + 3 falsche, bevorzugt aus schon Gelerntem
  const choiceOptions = useMemo(() => {
    if (!session || session.mode !== "quiz" || kind !== "choice" || !item) return [];
    const others = session.items.filter((it) => it.id !== item.id);
    const known = shuffle(others.filter((it) => it.introduced));
    const unknown = shuffle(others.filter((it) => !it.introduced));
    const distractors = [...known, ...unknown].slice(0, 3).map((it) => it.target);
    return shuffle([item.target, ...distractors]);
    // nur neu mischen, wenn eine neue Aufgabe kommt
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.mode, session?.queue, session?.pos]);

  // Bestandene Tests merken und als Wochenfortschritt speichern
  // (nur hinzufügen: „Nochmal von vorne üben“ löscht nichts)
  function savePasses(next) {
    const before = loadLocalPasses(localKey);
    saveLocalPasses(
      localKey,
      [...new Set([...before.passed, ...next.passed])],
      before.finalPassed || next.finalPassed
    );
    const value = topicWeekProgress(next, weeklyTarget);
    if (userId && value > savedCountRef.current) {
      savedCountRef.current = value;
      savePhraseProgress(userId, profile, langId, paceValue, value, currentWeek).catch(
        (e) => console.warn("TopicPractice: savePhraseProgress failed:", e)
      );
    }
  }

  function resetQuestionUI() {
    setSelected(null);
    setTyped("");
    setChecked(false);
    setCorrect(false);
  }

  function goTo(next) {
    resetQuestionUI();
    setTestInputs({});
    setTestResult(null);
    testInputRefs.current = [];
    setSession(next);
  }

  const handleCheck = () => {
    if (!item) return;
    const given = kind === "choice" ? selected : typed;
    const ok = normalize(given) === normalize(item.target);
    setChecked(true);
    setCorrect(ok);
    setSession(answerQuestion(session, ok));
  };

  const handleTestCheck = () => {
    const results = {};
    session.queue.forEach((idx) => {
      results[idx] = normalize(testInputs[idx]) === normalize(session.items[idx].target);
    });
    const graded = gradeTest(session, results);
    setTestResult({ results, passed: graded.passed, errors: graded.errors });
    setSession(graded.session);
    if (graded.passed) savePasses(graded.session);
  };

  const testTitle = (key) =>
    key === "final"
      ? weekPlan.finalTitle
      : weekPlan.blocks.find((b) => b.key === key)?.title || key;

  const title = `${weekPlan.title} – Woche ${currentWeek}`;
  const backButton = (
    <Button block variant="secondary" onClick={onBack}>
      Zurück zum Hauptmenü
    </Button>
  );

  if (loading) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note>fluent! lädt deine Übungen …</Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  if (error || !session || session.items.length === 0) {
    return (
      <>
        <ScreenHeader title={title} />
        <Note tone="error">
          {error ? `Fehler: ${error}` : "Keine Einträge für diese Woche gefunden."}
        </Note>
        <div className="exercise-actions">{backButton}</div>
      </>
    );
  }

  // Leiste mit allen großen Tests der Woche (grün = bestanden)
  const testBadges = (
    <div className="topic-tests">
      {weekPlan.blocks.map((b) => (
        <Badge key={b.key} tone={session.passed.includes(b.key) ? "mint" : "neutral"}>
          {session.passed.includes(b.key) ? "✓ " : ""}
          {b.title}
        </Badge>
      ))}
      <Badge tone={session.finalPassed ? "mint" : "neutral"}>
        {session.finalPassed ? "✓ " : ""}
        {weekPlan.finalTitle}
      </Badge>
    </div>
  );

  // ---------- Woche abgeschlossen ----------
  if (session.mode === "done") {
    return (
      <>
        <ScreenHeader
          title={`${weekPlan.title}: geschafft 🎉`}
          subtitle="Du hast alle Tests dieser Woche bestanden. Super Arbeit!"
        />
        {testBadges}
        <Stack gap={3} className="exercise-actions">
          <Button
            block
            onClick={() =>
              goTo(startTopicSession(weekPlan, session.items, [], false))
            }
          >
            Nochmal von vorne üben
          </Button>
          {backButton}
        </Stack>
      </>
    );
  }

  // ---------- Großer Test ----------
  if (session.mode === "test") {
    const isFinal = session.testBlock === "final";
    const allowed = allowedTestErrors(session.queue.length);
    return (
      <>
        <ScreenHeader
          title={`${isFinal ? "Abschluss-Test" : "Großer Test"}: ${testTitle(session.testBlock)}`}
          subtitle={`Schreib alles auf ${langLabel}. Höchstens ${allowed} Fehler erlaubt.`}
        />
        {testBadges}

        <Stack gap={2} className="topic-test">
          {session.queue.map((idx, i) => {
            const it = session.items[idx];
            const result = testResult ? testResult.results[idx] : undefined;
            return (
              <div key={it.id} className="topic-test__row">
                <span className="topic-test__prompt">{it.src}</span>
                <div className="topic-test__answer">
                  <TextInput
                    ref={(el) => {
                      testInputRefs.current[i] = el;
                    }}
                    type="text"
                    value={testInputs[idx] || ""}
                    disabled={!!testResult}
                    onChange={(e) =>
                      setTestInputs((prev) => ({ ...prev, [idx]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      const nextInput = testInputRefs.current[i + 1];
                      if (nextInput) nextInput.focus();
                      else if (!testResult) handleTestCheck();
                    }}
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    aria-label={`${langLabel} für ${it.src}`}
                    status={result === undefined ? undefined : result ? "correct" : "wrong"}
                  />
                  {result === false && (
                    <span className="topic-test__solution">{it.target}</span>
                  )}
                </div>
              </div>
            );
          })}
        </Stack>

        {testResult && (
          <Note
            tone={testResult.passed ? "success" : "error"}
            className="exercise-feedback"
          >
            {testResult.passed
              ? `Bestanden! ${testResult.errors === 0 ? "Alles richtig." : `${testResult.errors} Fehler.`} 🎉`
              : `${testResult.errors} Fehler – noch nicht bestanden. Wir üben weiter, der Test kommt später nochmal.`}
          </Note>
        )}

        <div className="exercise-actions">
          {!testResult ? (
            <Button block onClick={handleTestCheck}>
              Test prüfen
            </Button>
          ) : (
            <Button block onClick={() => goTo(continueAfterTest(session))}>
              Weiter
            </Button>
          )}
        </div>
        <div className="exercise-actions-tight">{backButton}</div>
      </>
    );
  }

  // ---------- Kennenlernen ----------
  if (session.mode === "intro") {
    return (
      <>
        <ScreenHeader
          title={title}
          subtitle="Neu: Präg dir das ein, gleich wird es abgefragt"
        />
        {testBadges}
        <Panel variant="muted" className="exercise-prompt">
          <Badge tone="sun">
            Neu {session.pos + 1} von {session.queue.length}
          </Badge>
          <div className="exercise-prompt__text">{item.src}</div>
          <div className="exercise-prompt__solution">{item.target}</div>
          <SpeakButton text={item.target} lang={learningLang} />
        </Panel>
        <Button block onClick={() => goTo(confirmIntro(session))}>
          Verstanden
        </Button>
        <div className="exercise-actions-tight">{backButton}</div>
      </>
    );
  }

  // ---------- Abfrage ----------
  return (
    <>
      <ScreenHeader
        title={title}
        subtitle={kind === "choice" ? "Wähle die richtige Übersetzung aus" : `Schreib es auf ${langLabel}`}
      />
      {testBadges}
      <Panel variant="muted" className="exercise-prompt">
        <Badge tone="blue">
          Abfrage {session.pos + 1} von {session.queue.length}
        </Badge>
        <div className="exercise-prompt__text">{item.src}</div>
      </Panel>

      <Stack gap={3}>
        {kind === "choice" ? (
          <Stack gap={2}>
            {choiceOptions.map((opt) => {
              const isRight = checked && normalize(opt) === normalize(item.target);
              const isWrongPick = checked && selected === opt && !isRight;
              return (
                <OptionCard
                  key={opt}
                  label={opt}
                  active={selected === opt}
                  tone={isRight ? "right" : isWrongPick ? "wrong" : undefined}
                  disabled={checked}
                  onClick={() => setSelected(opt)}
                />
              );
            })}
          </Stack>
        ) : (
          <TextInput
            type="text"
            value={typed}
            readOnly={checked}
            autoFocus
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              // Enter: erst prüfen, dann weiter
              if (e.key !== "Enter") return;
              if (!checked && typed.trim()) handleCheck();
              else if (checked) goTo(nextQuestion(session));
            }}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck="false"
            aria-label={`Antwort auf ${langLabel}`}
            status={checked ? (correct ? "correct" : "wrong") : undefined}
          />
        )}

        {checked && (
          <Note tone={correct ? "success" : "error"} className="exercise-feedback">
            {correct ? "Richtig! 🎉" : (
              <>
                Richtig wäre: <strong>{item.target}</strong>. Kommt bald nochmal dran.
              </>
            )}
          </Note>
        )}

        {!checked ? (
          <Button
            block
            onClick={handleCheck}
            disabled={kind === "choice" ? selected == null : !typed.trim()}
          >
            Prüfen
          </Button>
        ) : (
          <Button block onClick={() => goTo(nextQuestion(session))}>
            Weiter
          </Button>
        )}
      </Stack>

      <div className="exercise-actions-tight">{backButton}</div>
    </>
  );
}
